import { DragEvent, useCallback, useMemo } from 'react';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

import { AccessorKeyColumnDef } from '@tanstack/table-core';
import { FILTERABLE_FIELDS } from '../../state/filtering.state';
import { HideCheckbox } from './HideCheckbox';
import { Item } from '../../types/item.types';
import { SubItem } from '../../types/sub-item.types';
import has from 'lodash/has';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useHiddenColumnState } from '../../state/column-hiding.state';
import { useTableDataState } from '../../state/table-data.state';

type ColumnDef = AccessorKeyColumnDef<Item> | AccessorKeyColumnDef<SubItem>;
type ColumnId = keyof Item | keyof SubItem;

// Column to filter field mapping
export const COLUMN_FILTER_MAPPING: Partial<Record<ColumnId, string[]>> = {
   // Item primary fields
   'itemNo': ['itemNos', 'search'],
   'itemName': ['itemName', 'search'],
   'buCodePru': ['buCodePru'],
   'buCodePdu': ['buCodePdu'],
   'hfb': ['hfbs'],
   'pa': ['pas'],
   'familyName': ['familyName'],
   'itemState': ['itemState'],
   'progress': ['progressState'],
   'piaImportStatus': ['piaImportStatus'],
   'saleStartDateGlobal': ['ssd'],
   'saleEndDateGlobal': ['eds'],
   'isBase': ['isBase'],
   'dwpDeviation': ['dwpDeviates'],
   'isDwpDeviationWithinTolerance': ['dwpDeviates'],

   // SubItem fields
   'part': ['part'],
   'partDetail': ['partDetail'],
   'materialGradeId': ['materialGradeId'],
   'materialGrade': ['materialGradeId', 'search2'],
   'comment': ['comment'],
   'updatedBy': ['updatedBy'],
   'locked': ['locked', 'unlocked'],

   // Special filters
   'parentItemNo': ['excludeConnected'],  // Filters for items where parentItemNo == null (loose items)
   'isBasedOnOtherItem': ['excludeConnected']  // Inverse filter - set to find items that AREN'T based on other items
   //Todo : handle missingGradeId
};

export const findRelatedFilterFields = (colId: string): string[] => {
   if (colId in COLUMN_FILTER_MAPPING) {
      return COLUMN_FILTER_MAPPING[colId as ColumnId] || [];
   }

   if (colId in FILTERABLE_FIELDS) {
      return [colId];
   }

   return [];
};

interface HideListItemProps {
   column: ColumnDef;
   index: number;
   isDragOver: boolean;
   columnsMoveable: boolean;
   onDragStart: (index: number) => void;
   onDragOver: (index: number) => void;
   onDrop: (index: number) => void;
   onDragEnd: () => void;
}

export const HideSubListItem = ({ column, index, isDragOver, columnsMoveable, onDragStart, onDragOver, onDrop, onDragEnd }: HideListItemProps) => {
   const hiddenColumns = useHiddenColumnState((s) => s.hiddenColumns);
   const toggleColumn = useHiddenColumnState((s) => s.toggleColumn);

   const query = useTableDataState((s) => s.query);
   const setQuery = useTableDataState((s) => s.setQuery);

   const columnId = column.accessorKey;

   const relatedFilterFields = useMemo(() =>
      findRelatedFilterFields(columnId),
      [columnId]
   );

   const handleToggleClick = useCallback(() => {
      const isVisible = hiddenColumns[columnId] ?? true;

      if (isVisible) {
         const newQuery = { ...query };
         const sortingByThisColumn = query.orderBy === columnId;

         if (sortingByThisColumn) {
            newQuery.orderBy = undefined;
            newQuery.desc = undefined;
         }

         let filtersChanged = false;
         relatedFilterFields.forEach(field => {
            if (has(query, field)) {
               delete (newQuery as Record<string, unknown>)[field];
               filtersChanged = true;
            }
         });

         if (sortingByThisColumn || filtersChanged) {
            setQuery(newQuery);
         }
      }

      toggleColumn(columnId);
   }, [columnId, hiddenColumns, query, relatedFilterFields, setQuery, toggleColumn]);

   const handleDragStart = useCallback((e: DragEvent<HTMLLIElement>) => {
      e.dataTransfer.effectAllowed = 'move';
      const el = e.currentTarget;
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.width = `${el.offsetWidth}px`;
      clone.style.background = theme.colors.bg.primary;
      clone.style.boxShadow = tokens.shadow.md;
      clone.style.position = 'absolute';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      requestAnimationFrame(() => document.body.removeChild(clone));
      onDragStart(index);
   }, [index, onDragStart]);

   const handleDragOver = useCallback((e: DragEvent<HTMLLIElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      onDragOver(index);
   }, [index, onDragOver]);

   const handleDrop = useCallback((e: DragEvent<HTMLLIElement>) => {
      e.preventDefault();
      onDrop(index);
   }, [index, onDrop]);

   return (
      <ListItem
         key={`column-${columnId}`}
         disablePadding
         draggable={columnsMoveable}
         onDragStart={handleDragStart}
         onDragOver={handleDragOver}
         onDrop={handleDrop}
         onDragEnd={onDragEnd}
         style={{
            borderTop: isDragOver ? `${tokens.border.thick} ${theme.colors.border.focus}` : `${tokens.border.thick} transparent`,
            cursor: columnsMoveable ? 'grab' : 'default',
         }}
      >
         <ListItemButton
            role={undefined}
            dense
            onClick={handleToggleClick}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: columnsMoveable ? 1 : 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
               <HideCheckbox column={column} />
               <ListItemText
                  style={{ paddingLeft: tokens.space.lg, margin: 0, marginRight: tokens.space.xl }}
                  id={`column-${columnId}`}
                  primary={`${column.header}`} />
            </div>
            <ListItemIcon style={{ minWidth: columnsMoveable ? 28 : 0, margin: 0 }}>
               {columnsMoveable && <span style={{ cursor: 'grab', fontSize: tokens.font.size.lg, color: theme.colors.text.tertiary }}>⠿</span>}
            </ListItemIcon>
         </ListItemButton>
      </ListItem>
   );
};
